import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, Mail, Check, Pencil } from 'lucide-react';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';

/**
 * UserFormConfirmV2Scene — email-confirmation MODAL over a dimmed Review page.
 * Matches the user-provided reference: a centered white modal with a grey
 * circle + mail envelope glyph, explainer copy, an email display field and a
 * teal "Confirm ✓" button.
 */

function ReviewFieldGhost({ label, value }) {
  return (
    <div>
      <p className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5">{label}<span className="text-red-500">*</span></p>
      <p className="text-[13.5px] font-body text-[#374151]">{value}</p>
    </div>
  );
}

export default function UserFormConfirmV2Scene({ onNext }) {
  const cursorPath = [
    { x: 660, y: 760, delay: 2.0, click: true },   // click Confirm
  ];

  return (
    <PortalShellV2 variant="headerOnly" fullOverlay={<AutoCursor targets={cursorPath} startDelay={1.8} />}>
      <div data-testid="scene-user-form-confirm-v2" className="relative">

        {/* ---- Dimmed Review page in background ---- */}
        <div className="px-16 pt-8 pb-12 opacity-40 pointer-events-none select-none">
          <div className="max-w-5xl mx-auto">
            <div className="text-[12.5px] font-body text-[#6B7280] mb-2 flex items-center gap-1">
              Manage <ChevronRight size={11} /> Roles <ChevronRight size={11} /> <span className="text-[#0A0A0A]">Create new role</span>
            </div>

            <h1 className="font-heading font-extrabold text-[42px] leading-tight tracking-tight text-[#0A0A0A]">Review user details</h1>
            <p className="mt-1 font-body text-[13px] text-[#6B7280]">Please review the details of this user, an link will be sent to confirm their details.</p>

            <div className="flex justify-end -mt-8 mb-2">
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E7EB] text-[12px] font-body text-[#0A0A0A]">
                <Pencil size={12} /> Edit
              </button>
            </div>

            <div className="border-b border-[#E5E7EB] mt-4 mb-8" />

            <h2 className="font-heading font-extrabold text-[24px] text-[#0A0A0A] mb-6">Basic details</h2>
            <div className="grid grid-cols-2 gap-x-10 gap-y-6 mb-8">
              <ReviewFieldGhost label="User's first name"              value="Abdul" />
              <ReviewFieldGhost label="User's business email address"  value="abdulahmed@abc.com" />
              <div>
                <p className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5">User type</p>
                <p className="text-[13.5px] font-body text-[#374151]">Non-clinician</p>
              </div>
              <div />
              <div>
                <p className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5">Document type</p>
                <p className="text-[13.5px] font-body text-[#374151]">Emirates ID</p>
              </div>
              <div>
                <p className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5">Document number</p>
                <p className="text-[13.5px] font-body text-[#374151]">748-1234-1234567-1</p>
              </div>
              <div>
                <p className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5">Document expiry date</p>
                <p className="text-[13.5px] font-body text-[#374151]">12 Dec' 27</p>
              </div>
              <div>
                <p className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5">Linked facility</p>
                <p className="text-[13.5px] font-body text-[#374151]">Rashid hospital, UAE</p>
              </div>
            </div>

            <h2 className="font-heading font-extrabold text-[24px] text-[#0A0A0A]">Assigned roles</h2>
          </div>
        </div>

        {/* ---- Full-area dim overlay ---- */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}
          className="absolute inset-0 bg-black/45"
        />

        {/* ---- Modal ---- */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 flex items-center justify-center p-6"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-10 py-10 relative">
            <button
              data-testid="confirm-modal-close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-[#F3F4F6]"
            >
              <X size={16} />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-[#E5E7EB] flex items-center justify-center mb-5">
                <Mail size={24} className="text-sky-500" strokeWidth={2.2} />
              </div>

              <p className="font-body text-[13.5px] text-[#374151] leading-relaxed mb-1">
                We'll send an email to this address to help the user sign in.
              </p>
              <p className="font-body text-[13.5px] text-[#374151] leading-relaxed mb-5">
                Please confirm it's correct.
              </p>

              <div className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-[14px] font-body font-semibold text-[#0A0A0A] text-center mb-6">
                abdulahmed@xyz.com
              </div>

              <motion.button
                data-testid="confirm-modal-submit"
                onClick={onNext}
                animate={{
                  scale: [1, 1, 1.08, 0.97, 1.05, 1],
                  boxShadow: [
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 10px rgba(13,148,136,0.3)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 8px rgba(13,148,136,0.25)',
                    '0 0 0 0 rgba(13,148,136,0)',
                  ],
                }}
                transition={{ duration: 2.4, delay: 1.2, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
                className="flex items-center gap-2 px-10 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-[13px] hover:bg-teal-700"
              >
                Confirm <Check size={15} />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </PortalShellV2>
  );
}
